import { useEffect, useRef, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import Modal from '../../../common/Modal';
import PeriodSelector from '../../../common/PeriodSelector';
import formatReportPeriod from '../../../../helpers/liveReportHelpers/formatReportPeriod';
import useDebouncedWrite from '../../../../hooks/useDebouncedWrite';
import useSettings from '../../../../hooks/Settings';
import useSharedCollection from '../../../../firebase/useSharedCollection';
import ReportPersonaButton from './ReportPersonaButton';
import TemplateModulesMetaItem from './TemplateModulesMetaItem';
import ReportPersonaSelector from './ReportPersonaSelector';

/** @typedef {import('../../../../types/LiveReports2').LiveReports2Doc} LiveReports2Doc */

/**
 * 'Deelnemer:' in de kopregel, naast 'Periode', met dezelfde kiezer als de
 * kolom 'Deelnemer' in de rapportagetabel (B3, /test 2026-10-20). Alleen met
 * de module Deelnemers; zonder module ook geen Personas-listener.
 *
 * @param {object} props
 * @param {LiveReports2Doc} props.report - Het rapport (geen sjabloon)
 * @returns {JSX.Element|null}
 */
const ReportPersonaMetaItem = ({ report }) => {
    const { personasOn } = useSettings();
    const personasEnabled = personasOn === 'flex';
    const personas = useSharedCollection(personasEnabled ? 'Personas' : null);
    const [modalOpen, setModalOpen] = useState(false);

    if (!personasEnabled) return null;

    return (
        <div className="template-viewer-meta-item">
            <span className="meta-label">Deelnemer:</span>
            <ReportPersonaButton report={report} personas={personas} onClick={() => setModalOpen(true)} />
            {/* Per opening opnieuw gemount: het concept begint bij wat opgeslagen is. */}
            {modalOpen && (
                <ReportPersonaSelector
                    modalOpen={true}
                    setModalOpen={setModalOpen}
                    report={report}
                    personas={personas}
                />
            )}
        </div>
    );
};

/**
 * Info card component displaying report details and actions.
 * @param {object} props
 * @param {LiveReports2Doc} [props.report] - The report document
 * @param {() => void} [props.handleDuplicate] - Handler for the 'Kopiëren' button: copies this report (or template) as 'Kopie van <naam>', same as the 'Kopiëren' column in the list
 * @param {() => void} [props.handleNewFromTemplate] - Handler for creating new report from template
 * @param {(report: LiveReports2Doc) => void} [props.handleEdit] - Handler for editing the report
 * @param {(report: LiveReports2Doc) => void} [props.handleSetStatus] - Handler for changing the report status (publish/unpublish)
 * @param {(open: boolean) => void} [props.setDeleteModalOpen] - Handler for opening delete modal
 * @param {() => void} [props.handleShare] - Opens the 'Delen' window (link, code, PDF), same as the share icon in the 'Delen' column of the list. Only usable for a published report, like that icon; on a draft it stays in place, disabled, with the reason as its title.
 * @param {boolean} [props.deletePending=false] - Whether deletion is running (after the delete modal confirmed) — shows a spinner in the delete button
 * @param {(newTitle: string) => void} [props.onReportNameChange] - Handler for changing the report title. When provided, the title renders as an editable input; otherwise it's a read-only heading.
 * @param {boolean} [props.editable=false] - Whether the report is editable (admin only features)
 * @param {(modules: Array<string>) => Promise<void>} [props.onRequiredModulesChange] - Template builder only: writes `RequiredModules` (which modules a tenant needs to see the template). Without it, no checkboxes.
 */
export const LiveReportInfoCard = ({
    report,
    handleDuplicate,
    handleNewFromTemplate,
    handleEdit,
    handleSetStatus,
    setDeleteModalOpen,
    handleShare,
    deletePending = false,
    onReportNameChange,
    editable = false,
    onRequiredModulesChange,
}) => {

    const [periodModalOpen, setPeriodModalOpen] = useState(false);

    /* The title is typed into a local draft, not straight into `report.Name`:
       that value only comes back via a Firestore snapshot, so a controlled
       input on it is reset after every keystroke and only the last character
       survives. The draft is saved once the user pauses (and on leave), not
       per keystroke.
       AI-CONTEXT: See docs/pitfalls.md#state-per-keystroke-write-loses-the-tail */
    const [nameDraft, setNameDraft] = useState(report?.Name || '');
    /** @type {React.RefObject<HTMLInputElement>} */
    const nameInputRef = useRef(null);
    const { save: scheduleNameSave } = useDebouncedWrite(
        (_key, /** @type {string} */ newName) => onReportNameChange && onReportNameChange(newName)
    );

    /* Follow a name changed elsewhere (another tab, a duplicate we navigated
       to) — but not while the user is typing, or the echo of an earlier save
       would overwrite newer keystrokes. */
    useEffect(() => {
        if (document.activeElement === nameInputRef.current) return;
        setNameDraft(report?.Name || '');
    }, [report?.docid, report?.Name]);

    /* Which async action is in flight — its button shows a spinner, all
       action buttons are disabled so a slow duplicate/publish can't be
       double-fired. */
    const [pendingAction, setPendingAction] = useState(/** @type {'status' | 'duplicate' | 'newFromTemplate' | null} */ (null));

    /* Duplicate/new-from-template handlers navigate away on success, so this
       component can unmount before the handler resolves — guard the state
       reset (React 17 warns on setState after unmount). */
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    /**
     * @param {'status' | 'duplicate' | 'newFromTemplate'} action
     * @param {(...args: any[]) => void | Promise<void>} handler
     * @param {...any} args - Forwarded to the handler.
     */
    const runAction = async (action, handler, ...args) => {
        if (pendingAction) return;
        setPendingAction(action);
        try {
            await handler(...args);
        } finally {
            if (isMounted.current) setPendingAction(null);
        }
    };

    /**
     * The button's icon, swapped for a spinner while its action runs.
     * @param {'status' | 'duplicate' | 'newFromTemplate'} action
     * @param {React.ReactNode} icon
     */
    const iconOrSpinner = (action, icon) => pendingAction === action
        ? <span className="template-action-button-spinner" aria-hidden="true" />
        : icon;

    /* Deletion is owned by the parent page (it runs after the DeleteModal
       confirms), so it blocks the buttons via the deletePending prop. */
    const actionBlocked = !!pendingAction || deletePending;

    if (!report) {
        return null;
    }

    return (
        <div className="template-viewer-info-card">
            <div className="template-viewer-details">
                <div className="template-viewer-title-section">
                    {onReportNameChange ? (
                        <input
                            type="text"
                            ref={nameInputRef}
                            value={nameDraft}
                            onChange={e => {
                                const newName = e.target.value;
                                setNameDraft(newName);
                                scheduleNameSave('Name', newName);
                            }}
                            placeholder="Rapportage naam"
                            className="template-title-input"
                        />
                    ) : (
                        <h2>{report?.Name || 'Naamloos'}</h2>
                    )}
                </div>

                <div className="template-viewer-meta">
                    <div className="template-viewer-meta-item">
                        <span className="meta-label">Type:</span>
                        <span className="meta-value">
                            {report?.IsTemplate
                                ? 'Template'
                                : 'Live rapportage'}
                        </span>
                    </div>
                    <div className="template-viewer-meta-item">
                        <span className="meta-label">Status:</span>
                        <span className="meta-value">
                            {report?.Status === 'published'
                                ? 'Gepubliceerd'
                                : 'Concept'}
                        </span>
                    </div>
                    <div className="template-viewer-meta-item">
                        <span className="meta-label">Periode:</span>
                        {editable ? (
                            <button
                                type="button"
                                className="reports-period-button"
                                title="Periode instellen"
                                aria-label="Periode instellen"
                                onClick={() => setPeriodModalOpen(true)}
                            >
                                <CalendarTodayOutlinedIcon />
                                <span>{formatReportPeriod(report)}</span>
                            </button>
                        ) : (
                            <span className="meta-value">{formatReportPeriod(report)}</span>
                        )}
                    </div>
                    {!report.IsTemplate && <ReportPersonaMetaItem report={report} />}
                    {report.IsTemplate && onRequiredModulesChange && (
                        <TemplateModulesMetaItem modules={report.RequiredModules} onChange={onRequiredModulesChange} />
                    )}
                    <div className="template-viewer-meta-item">
                        <span className="meta-label">Aangemaakt op:</span>
                        <span className="meta-value">
                            {report?.CreatedAt
                                ? new Date(report.CreatedAt.seconds * 1000).toLocaleDateString('nl-NL', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })
                                : 'Onbekend'}
                        </span>
                    </div>
                    <div className="template-viewer-meta-item">
                        <span className="meta-label">Gewijzigd op:</span>
                        <span className="meta-value">
                            {report?.UpdatedAt
                                ? new Date(report.UpdatedAt.seconds * 1000).toLocaleDateString('nl-NL', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                })
                                : 'Onbekend'}
                        </span>
                    </div>
                </div>

                <div className="template-viewer-actions">
                    {report?.IsTemplate ? (
                        <>
                            {!editable && <button
                                    className="template-action-button primary"
                                    onClick={() => runAction('newFromTemplate', handleNewFromTemplate)}
                                    disabled={actionBlocked}
                                >
                                    {iconOrSpinner('newFromTemplate', <ContentCopyIcon />)}
                                    <span>Deze template gebruiken</span>
                                </button>
                            }
                        </>
                    ) : (
                        <>
                            {handleSetStatus && (
                                <button
                                    className={`template-action-button ${report.Status === 'published' ? 'secondary' : 'primary'}`}
                                    onClick={() => runAction('status', handleSetStatus, report)}
                                    disabled={actionBlocked}
                                >
                                    {report.Status === 'published'
                                        ? <>{iconOrSpinner('status', <UnpublishedIcon />)} <span>Concept maken</span></>
                                        : <>{iconOrSpinner('status', <PublishedWithChangesIcon />)} <span>Publiceren</span></>}
                                </button>
                            )}
                            {handleEdit && <button
                                    className="template-action-button primary"
                                    onClick={() => report && handleEdit(report)}
                                    disabled={actionBlocked}
                                >
                                    <EditIcon />
                                    <span>Bewerken</span>
                                </button>
                            }
                        </>
                    )}

                    {/* Alleen te gebruiken bij een gepubliceerde rapportage, net
                        als het deel-icoon in de lijst: de open link en de PDF
                        werken bij een concept niet. Bij een concept blijft de
                        knop wél staan, uitgeschakeld: verdween hij, dan schoof
                        'Kopiëren' op zijn plek en maakte wie op geheugen klikt
                        een kopie (testronde 2026-10-28). */}
                    {handleShare && !report.IsTemplate && <button
                            className="template-action-button primary"
                            onClick={handleShare}
                            disabled={actionBlocked || report.Status !== 'published'}
                            title={report.Status === 'published' ? undefined : 'Publiceer de rapportage eerst om hem te delen'}
                        >
                            <ShareOutlinedIcon />
                            <span>Delen</span>
                        </button>
                    }

                    {handleDuplicate && <button
                            className="template-action-button primary"
                            onClick={() => runAction('duplicate', handleDuplicate)}
                            disabled={actionBlocked}
                        >
                            {iconOrSpinner('duplicate', <ContentCopyIcon />)}
                            <span>Kopiëren</span>
                        </button>
                    }

                    {setDeleteModalOpen && <button
                            className="template-action-button danger"
                            onClick={() => setDeleteModalOpen(true)}
                            disabled={actionBlocked}
                        >
                            {deletePending
                                ? <span className="template-action-button-spinner" aria-hidden="true" />
                                : <DeleteIcon />}
                            <span>Verwijderen</span>
                        </button>
                    }
                </div>

                {(report?.IsTemplate && handleEdit) && (
                    <div className="template-viewer-actions">
                        <button
                            className="template-action-button secondary"
                            onClick={() => handleEdit(report)}
                            disabled={actionBlocked}
                        >
                            <EditIcon />
                            <span>Admin only: template bewerken</span>
                        </button>
                    </div>
                )}
            </div>

            {editable && (
                <Modal
                    modalOpen={periodModalOpen}
                    setModalOpen={setPeriodModalOpen}
                    pageHeader="Periode"
                    description="Bepaal welke periode de data in deze rapportage toont. Widgets kunnen hier per widget van afwijken."
                    /* PeriodSelector saves straight to Firestore on change;
                       Opslaan only closes the modal (familiar footer). */
                    confirm={() => setPeriodModalOpen(false)}
                    cancelLabel="Sluiten"
                    content={
                        <PeriodSelector
                            page="livereport2"
                            docid={report.docid}
                            currentStart={report.Start}
                            currentEnd={report.End}
                        />
                    }
                />
            )}
        </div>
    );
};
